/**
 * Entity-workspace sidebar — the takeover counterpart of AdminSidebar.
 *
 * When an org or user page takes over the chrome, this renders in the global
 * sidebar's exact slot with its exact shell (width, list styling) so the
 * takeover swaps the sidebar's content, not its look. The header is a
 * context stack showing the full global → org → user ancestry: each
 * ancestor row is a one-click jump to that level, and the last row is the
 * current entity — bold, with a chip naming its level — so it's always
 * visible which level you're on.
 */

import { Fragment } from 'react';
import { OpenInNew, SubdirectoryArrowRight } from '@mui/icons-material';
import { Box, Chip, List, ListItemButton, ListItemText, Typography } from '@mui/material';

import type { NavGroup } from './types';

export interface ContextStackEntry {
  /** Display name: "Admin v2", the org's name, or the user's name. */
  label: string;
  /** Level chip shown on the current (last) entry, e.g. "Org" or "User". */
  chip?: string;
  /** Navigate to this level (for the current entry: back to its overview). */
  onSelect: () => void;
}

export interface WorkspaceSidebarProps {
  groups: NavGroup[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Ancestry from global down to the current entity (last entry = current). */
  contextStack: ContextStackEntry[];
}

export function WorkspaceSidebar({
  groups,
  activeKey,
  onSelect,
  contextStack,
}: WorkspaceSidebarProps) {
  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full shrink-0">
      {/* Context stack — replaces AdminSidebar's brand band. Ancestors are
          quiet, clickable rows; the current entity is bold with its level
          chip and a primary accent bar (mirrors the selected-nav styling). */}
      <div className="border-b border-gray-200 py-2">
        {contextStack.map((entry, index) => {
          const isCurrent = index === contextStack.length - 1;
          return (
            <button
              key={`${entry.label}-${index}`}
              type="button"
              onClick={entry.onSelect}
              title={entry.label}
              className={
                isCurrent
                  ? 'w-full flex items-center gap-1.5 px-3 py-1.5 text-left cursor-pointer border-l-4 border-blue-600 bg-blue-50/60 hover:bg-blue-100/60'
                  : 'w-full flex items-center gap-1.5 px-3 py-1 text-left cursor-pointer border-l-4 border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-800'
              }
              style={{ paddingLeft: `${12 + index * 14}px` }}
            >
              {index > 0 && (
                <SubdirectoryArrowRight
                  sx={{ fontSize: 14 }}
                  className={isCurrent ? 'text-blue-600 shrink-0' : 'text-gray-400 shrink-0'}
                />
              )}
              <span
                className={
                  isCurrent
                    ? 'min-w-0 truncate text-base font-semibold text-gray-900'
                    : 'min-w-0 truncate text-sm'
                }
              >
                {entry.label}
              </span>
              {entry.chip && (
                <Chip
                  label={entry.chip}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 11, flexShrink: 0 }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        <List component="nav" disablePadding>
          {groups.map(group => (
            <Fragment key={group.label}>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
              </Box>
              {group.items.map(item => {
                const active = item.key === activeKey;
                return (
                  <ListItemButton
                    key={item.key}
                    onClick={() => onSelect(item.key)}
                    selected={active}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      py: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.50',
                        '&:hover': {
                          backgroundColor: 'primary.100',
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          color: active ? 'primary.main' : 'text.primary',
                          sx: {
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 400,
                          },
                        },
                      }}
                    />
                    {item.badge && (
                      <Chip
                        label={item.badge.label}
                        size="small"
                        color={
                          item.badge.tone === 'warn'
                            ? 'warning'
                            : item.badge.tone === 'success'
                              ? 'success'
                              : 'default'
                        }
                        variant={item.badge.tone === 'success' ? 'outlined' : 'filled'}
                        sx={{ height: 18, fontSize: 11 }}
                      />
                    )}
                    {item.external && (
                      <OpenInNew sx={{ fontSize: 13, color: 'text.disabled' }} />
                    )}
                  </ListItemButton>
                );
              })}
            </Fragment>
          ))}
        </List>
      </nav>
    </aside>
  );
}
